import React from "react";

const PayoutDetails = ({
  selectedPayout,
  editMode,
  eventId,
  allocationDifference,
  allocationOk = Math.abs(allocationDifference) <= 0.01,
  totalAllocated,
  localEventDetails,
  setLocalEventDetails,
}) => {
  // Early return if no payout data
  if (!selectedPayout || !selectedPayout.payouts) {
    return (
      <div className="mt-4 card-inset p-3">
        <p className="text-brand-muted text-sm">
          No payout configuration available for this event.
        </p>
      </div>
    );
  }
  const handlePrizeAmountChange = (eventId, place, value) => {
    try {
      const amount = parseFloat(value) || 0;
      const details = localEventDetails[eventId];

      // Check if selectedPayout exists and has payouts
      if (!details.selectedPayout || !details.selectedPayout.payouts) {
        console.error("No payout configuration found for event:", eventId);
        return;
      }

      // Update the amount for the specific place
      const updatedPayouts = details.selectedPayout.payouts.map((payout) =>
        payout.place === place ? { ...payout, amount } : payout
      );

      // Recalculate percentages based on the new amounts
      const payoutsWithRecalculatedPercentages = updatedPayouts.map(
        (payout) => ({
          ...payout,
          percentage:
            details.prizeFund > 0 ? payout.amount / details.prizeFund : 0,
        })
      );

      setLocalEventDetails((prev) => ({
        ...prev,
        [eventId]: {
          ...prev[eventId],
          selectedPayout: {
            ...prev[eventId].selectedPayout,
            payouts: payoutsWithRecalculatedPercentages,
            // Preserve the isFromDatabase flag - if it was saved before, it still is after manual edit
            isFromDatabase: prev[eventId].selectedPayout?.isFromDatabase ?? true,
          },
        },
      }));
    } catch (error) {
      console.error("Error updating prize amount:", error);
    }
  };

  return (
    <div className="mt-4">
      <h4 className="text-xs font-display font-bold tracking-wider uppercase text-brand-muted mb-2">
        Payout Details
      </h4>
      <div className="grid grid-cols-1 gap-2">
        {selectedPayout?.payouts
          ?.sort((a, b) => a.place - b.place)
          ?.map((payout) => (
            <div
              key={payout.place}
              className="flex justify-between items-center card-inset p-3"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-brand-charcoal">
                  Place {payout.place}
                </span>
                <span className="text-brand-muted font-score text-sm">
                  ({(payout.percentage * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="font-score font-medium text-brand-charcoal">
                {editMode ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={payout.amount || 0}
                    onChange={(e) =>
                      handlePrizeAmountChange(
                        eventId,
                        payout.place,
                        e.target.value
                      )
                    }
                    className="w-24 px-2 py-1 border border-brand-charcoal/20 rounded-lg font-score text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  />
                ) : (
                  `$${(payout.amount || 0).toFixed(2)}`
                )}
              </div>
            </div>
          ))}
      </div>
      {editMode && (
        <div className="mt-4 card-inset p-3">
          <div className="flex justify-between items-center">
            <span className="font-medium text-brand-charcoal">
              Total Allocated:
            </span>
            <span
              className={`font-score font-medium ${
                allocationOk ? "text-brand-charcoal" : "text-red-500"
              }`}
            >
              ${totalAllocated.toFixed(2)}
            </span>
          </div>
          {!allocationOk && (
            <div className="mt-1 text-sm text-red-500">
              {allocationDifference > 0
                ? "Over allocated by"
                : "Under allocated by"}{" "}
              ${Math.abs(allocationDifference).toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayoutDetails;
